import React from 'react'
import { Box, Icon, Text, Stack, Skeleton, StackProps } from '@chakra-ui/core'
import { VscArrowDown, VscArrowUp } from 'react-icons/vsc'

export type StatCardProps = {
  title: string
  icon: React.ElementType
  value: string | React.ReactNode
  trend?: {
    value: string
    type?: 'increase' | 'decrease'
  }
  loading?: boolean
  description?: string | React.ReactNode
}
export const StatCard: React.FC<StatCardProps> = ({
  title,
  icon,
  value,
  loading,
  trend,
  description
}) => {
  const trendValuePercentage = trend?.value?.split(' ')[0]
  const trendValueRest = trend?.value?.split(' ').slice(1).join(' ')
  return (
    <Skeleton isLoaded={!loading} borderRadius="md" my={2}>
      <Stack
        py={4}
        px={6}
        bg="white"
        borderRadius="md"
        alignItems="flex-start"
        justifyContent="space-between"
        direction="row"
      >
        <Stack direction="row" alignItems="center" spacing={4} flex={1}>
          <Stack direction="column" alignItems="flex-start" spacing={0}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                bg="green.100"
                p={1}
                borderRadius="full"
                color="green.500"
                w={8}
                h={8}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={icon} fontSize="md" />
              </Box>

              <Text fontSize="xs" fontWeight="500" minWidth="fit-content" color="gray.500">
                {title}
              </Text>
            </Stack>
            {typeof value === 'string' ? (
              <Text fontSize="xl">
                <Text as="span" fontWeight="extrabold" fontSize="2xl">
                  {value.split(' ')[0]}
                </Text>{' '}
                <Text as="span" fontSize="xs" color="gray.500">
                  {value.split(' ').slice(1).join(' ')}
                </Text>
              </Text>
            ) : (
              value
            )}

            {trend && (
              <Text fontSize="xs" fontWeight="600" pt={2}>
                <Stack
                  direction="row"
                  as="span"
                  display="inline-flex"
                  spacing={1}
                  p={1}
                  borderRadius="md"
                  {...(trend?.type && {
                    color: trend.type === 'increase' ? 'green.500' : 'red.500',
                    bg: trend.type === 'increase' ? 'green.50' : 'red.50'
                  })}
                >
                  {trend?.type && (
                    <Icon
                      as={trend?.type === 'increase' ? VscArrowUp : VscArrowDown}
                      fontSize="sm"
                      color={trend?.type === 'increase' ? 'green.500' : 'red.500'}
                    />
                  )}
                  <Text as="span">{trendValuePercentage}</Text>
                </Stack>
                <Text as="span" color="gray.500" mx={1} fontSize="x-small">
                  {trendValueRest}
                </Text>
              </Text>
            )}
            {description && (
              <Text fontSize="xs" pt={2} color="gray.500">
                {description}
              </Text>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Skeleton>
  )
}

export const Stats: React.FC<
  {
    stats: StatCardProps[]
    orientation?: 'horizontal' | 'vertical'
  } & StackProps
> = ({ stats, orientation, ...rest }) => {
  const content = stats.map((stat, index) => (
    <Box key={index} w="100%" minW={160} flex={1}>
      <StatCard {...stat} />
    </Box>
  ))
  return orientation === 'vertical' ? (
    <Stack direction="row" flexWrap="wrap" w="100%" {...rest}>
      {content}
    </Stack>
  ) : (
    <Stack direction="row" flexWrap="wrap" w="100%" {...rest}>
      {content}
    </Stack>
  )
}

export default Stats
